import ValidateModule from "./ValidateModule";

import type { IEventChannel, ApiOpts } from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type CacheModule from "./CacheModule";

const es = global.es;

/**
 * @class RouteModule
 */
export default class RouteModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;
    
    //private readonly VEHICLE_TYPES = ["car", "foot", "small-truck"];

    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param resourceName 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        // TODO FIXME Does this even make sense nowadays?
        eventChannel.addListener("onDestroy", () => {
            // prevent memory leaks when the map widget is destroyed
            this.eventChannel = this.rqBroker = this.resourceName = this.cache = null;
        });

        this.getLimit = this.getLimit.bind(this);
        this.createRouteLayerView = this.createRouteLayerView.bind(this);
        this.removeByGeoMapId = this.removeByGeoMapId.bind(this);
        this.getUrl = this.getUrl.bind(this);
        this.updateFeatureReferences = this.updateFeatureReferences.bind(this);
    }
    
    /**
     * Get the max number of waypoints this user is allowed have per route.
     * 
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the limit is loaded
     */
    getLimit(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/limit";
        
        return this.cache.doGet(this.rqBroker, this.resourceName, url, "_limit");
    }
    
    /**
     * @param routingRequest, see also: es.api.model.get("routing_Request") e.g. {"avoidFerries":true,"avoidHighways":false,"avoidTolls":false,"destination":{"x":46.59122000000001,"y":-112.004230},"language":"en","optimizeWaypoints":true,"origin":{"x":40.74560,"y":-73.94622000000001},"region":"es","travelMode":"DRIVING","measurementSystem":"METRIC","wayPoints":[{"x":1.0,"y":2.0},{"x":3.0,"y":4.0},{"x":5.0,"y":6.0}]}
     * @param geoMapId
     * @param opts
     * 
     * @returns RouteResponse
     */
    createRouteLayerView(routingRequest: object, geoMapId: number, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(routingRequest) ||
            !ValidateModule.checkId(geoMapId)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/" + geoMapId + "/layerView";

        return this.rqBroker.doPost({
            url: url,
            opts: opts,
            data: routingRequest
        }).then((rs) => {
            return es.api.appResources.setWorkingCopyChanged(geoMapId).then(() => {
                // note: no plan to cache these
                this.cache?.clear("LayerView");
                this.eventChannel?.fireEvent("onGetRoute", rs);

                return rs;
            });
        });
    }

    /**
     * removeByGeoMapId
     * 
     * @param geoMapId
     * @param isRetainConditionalStylers if true then only remove the route lv and leave the ConditionalStyler on related point layers.
     * @param opts
     * 
     * @returns
     */
    removeByGeoMapId(geoMapId: number, isRetainConditionalStylers = false, opts?: ApiOpts) {
        if (!ValidateModule.checkId(geoMapId)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/geomap/" + geoMapId + "?retain_conditional_stylers=" + isRetainConditionalStylers;

        return this.rqBroker.doDelete({
            url: url,
            opts: opts
        }).then((rs) => {
            return es.api.appResources.setWorkingCopyChanged(geoMapId).then(() => {
                this.eventChannel?.fireEvent("onRemoveRoute", rs);

                return rs;
            });
        });
    }
    
    /**
     * Get a URL for this route which can be displayed directly in Google Maps
     * 
     * @param routeLayerViewId 
     * @param opts 
     * 
     * @returns 
     */
    getUrl(routeLayerViewId: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(routeLayerViewId)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/" + routeLayerViewId + "/url";
        return this.rqBroker.doGet({url: url, opts: opts});
    }
    
    /**
     * 
     * @param routeLayerViewId 
     * @param featureReferences 
     * @param opts 
     * 
     * @returns 
     */
    updateFeatureReferences(routeLayerViewId: number, featureReferences, opts?: ApiOpts) {
        if (!ValidateModule.checkId(routeLayerViewId) ||
            !ValidateModule.checkMandatoryArg(featureReferences)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/" + routeLayerViewId + "/featureReferences";

        return this.rqBroker.doPut({
            url: url,
            data: featureReferences,
            opts: opts
        });
    }
}
